/**
 * Outreach showcase mode: boot the real storefront from an injected per-lead
 * payload (no tenant, no backend). Spec:
 * docs/superpowers/specs/2026-07-14-outreach-onDevice-real-app-design.md
 *
 * NOT related to components/showcase-new/ (internal dev component gallery).
 */

import type { ShowcasePayload, ShowcaseMenuItem } from '@restaurant/core'
export type { ShowcasePayload, ShowcaseMenuItem }

export type ShowcaseTokenRef = { token: string }

/**
 * Parse the showcase trigger out of a location.hash.
 * Returns the payload (hash mode), token sentinel (token mode), null (not showcase / disabled),
 * or 'invalid' (showcase/token hash present but undecodable — boot must surface the error marker).
 *
 * Hash mode encoding is PLAIN base64 of UTF-8 bytes — what the worker's
 * Buffer.toString('base64') emits. atob() alone yields latin-1
 * bytes-as-chars (mojibake for multi-byte names); decode via TextDecoder.
 */
export function detectShowcase(
  hash: string,
  hashEnabled: boolean,
  tokenEnabled: boolean,
): ShowcasePayload | ShowcaseTokenRef | null | 'invalid' {
  if (tokenEnabled && hash.startsWith('#token=')) {
    const token = hash.slice('#token='.length)
    return token ? { token } : 'invalid'
  }
  if (!hashEnabled) return null
  if (!hash.startsWith('#showcase=')) return null
  const b64 = hash.slice('#showcase='.length)

  // Reject base64url variants:
  // - Contains - or _ (base64url uses these instead of + and /)
  // - Length % 4 != 0 (base64url omits padding; plain base64 always has padding to 4-char boundary)
  if (b64.includes('-') || b64.includes('_') || b64.length % 4 !== 0) return 'invalid'

  try {
    const bytes = typeof atob === 'function'
      ? Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))
      : new Uint8Array(Buffer.from(b64, 'base64'))
    const data = JSON.parse(new TextDecoder('utf-8').decode(bytes))
    if (!data || typeof data.name !== 'string' || !Array.isArray(data.menu)) return 'invalid'
    return data as ShowcasePayload
  } catch {
    return 'invalid'
  }
}

// --- module-level state, resolved once per document -------------------------

function hashEnabled(): boolean {
  // Default OFF: an artifact built without the flag can never expose the raw
  // hash path (fail-safe for the future public preview deploy).
  return import.meta.env.VITE_SHOWCASE_HASH === '1'
}

function tokenEnabled(): boolean {
  // Default OFF: token mode is only active when explicitly enabled.
  return import.meta.env.VITE_SHOWCASE_TOKEN === '1'
}

const detected: ShowcasePayload | ShowcaseTokenRef | null | 'invalid' =
  typeof window === 'undefined' ? null : detectShowcase(window.location.hash, hashEnabled(), tokenEnabled())

/**
 * Stamp as early as module-eval — before React ever renders — so the
 * showcase-only iOS safe-area SIMULATION (apps/web/src/index.css, the
 * `html[data-showcase="1"]` rule) is active for the very first paint.
 *
 * Why it's needed: the outreach preview's Puppeteer screenshot (390x844, no
 * safe-area emulation) makes CSS env(safe-area-inset-top) resolve to 0, so
 * HeaderMenu's real-device inset (HeaderMenu.web.tsx) silently no-ops and the
 * header content (name/address/order-type toggle) renders under the phone
 * frame's dynamic-island art. The CSS rule forces the static
 * --safe-area-top token instead, gated on this attribute — never set outside
 * showcase mode, so real tenants are unaffected.
 */
if (typeof document !== 'undefined' && detected !== null) {
  document.documentElement.dataset.showcase = '1'
  // Which showcase path this document is on. Hash mode is the screenshot
  // harness (worker), token mode is the live, interactive preview page — a
  // distinction the CSS needs, because the harness must freeze motion to
  // capture settled pixels while the live page should keep its transitions.
  document.documentElement.dataset.showcaseMode = isTokenRef(detected) ? 'token' : 'hash'
}

function isTokenRef(d: typeof detected): d is ShowcaseTokenRef {
  return !!d && d !== 'invalid' && typeof (d as ShowcaseTokenRef).token === 'string'
}

/** True for the lifetime of this document — safe to branch hooks' effects on. */
export function isShowcase(): boolean {
  return detected !== null
}

/** The parsed payload, or null (includes the 'invalid' case and token mode — boot handles that). */
export function getShowcasePayload(): ShowcasePayload | null {
  return detected === 'invalid' || detected === null || isTokenRef(detected) ? null : detected
}

/** True when the hash was a showcase trigger but the payload failed to parse. */
export function isShowcaseInvalid(): boolean {
  return detected === 'invalid'
}

/** The token string when in token mode, or null otherwise. */
export function getShowcaseToken(): string | null {
  return isTokenRef(detected) ? detected.token : null
}

/** The embedder-declared device ('phone' | 'browser'), from the ?device=
 * query param. Gates the simulated status bar and tags the readiness
 * message — NEVER gate on layout mode (a narrow desktop iframe renders
 * mobile layout; layout-gating would paint an iOS status bar inside
 * browser chrome). */
export function showcaseDevice(): 'phone' | 'browser' | null {
  if (typeof window === 'undefined') return null
  const d = new URLSearchParams(window.location.search).get('device')
  return d === 'phone' || d === 'browser' ? d : null
}
